import React, { useState } from 'react';
import { SessionState, MaterializedView, AccountRecord } from '@/types';
import { api } from '@/services/api';
import {
    AccountBalance,
    PersonOutline,
    WarningAmber,
    History,
    CheckCircleOutline,
    OpenInFull,
    ChevronRight,
} from '@mui/icons-material';

interface AccountHistoryPanelProps {
    session: SessionState;
    materializedView: MaterializedView;
    onOpenFull: () => void;
    accentHex: string;
}

const STATUS_TONE: Record<string, string> = {
    'active': 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40',
    'at-risk': 'text-rose-300 bg-rose-500/15 border-rose-500/40',
    'renewal-pending': 'text-amber-300 bg-amber-500/15 border-amber-500/40',
    'complaint': 'text-rose-300 bg-rose-500/15 border-rose-500/40',
    'prospect': 'text-sky-300 bg-sky-500/15 border-sky-500/40',
};

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
    'key-account': 'Key Account',
    'prospect': 'Prospect',
    'complaint': 'Complaint Pending',
    'standard': 'Standard',
};

/**
 * Inline expandable cards for the account-focused dashboard layout. Each record
 * collapses to a header row; clicking it expands inline to show summary, key
 * contacts, open items, and recent activity. First click on an unviewed record
 * fires the view event so the BehaviorMetrics observation score credits the read.
 */
const AccountHistoryPanel: React.FC<AccountHistoryPanelProps> = ({
    session,
    materializedView,
    onOpenFull,
    accentHex,
}) => {
    const records = Object.values(materializedView.accountRecords || {});
    const [expandedId, setExpandedId] = useState<string | null>(records[0]?.id ?? null);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [localViewed, setLocalViewed] = useState<Record<string, boolean>>({});

    const handleExpand = async (record: AccountRecord) => {
        const willExpand = expandedId !== record.id;
        setExpandedId(willExpand ? record.id : null);
        if (!willExpand) return;
        if (record.hasBeenViewed || localViewed[record.id]) return;

        setPendingId(record.id);
        setLocalViewed((prev) => ({ ...prev, [record.id]: true }));
        try {
            await api.viewAccountRecord(session.id, record.id);
        } catch (err) {
            console.error('Failed to record account view', err);
        } finally {
            setPendingId(null);
        }
    };

    if (records.length === 0) {
        return (
            <section className="flex-1 min-h-[400px] bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col items-center justify-center text-slate-400">
                <AccountBalance style={{ fontSize: 56 }} className="mb-3 text-slate-500" />
                <p className="text-sm">No account records inherited.</p>
            </section>
        );
    }

    const unviewedCount = records.filter((r) => !(r.hasBeenViewed || localViewed[r.id])).length;

    return (
        <section className="flex-1 min-h-[400px] bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col relative overflow-hidden">
            <div className="flex items-center justify-between mb-4 border-b border-slate-700 pb-2">
                <div className="flex items-center gap-2">
                    <AccountBalance style={{ color: accentHex }} fontSize="small" />
                    <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: accentHex }}>Account History</h3>
                    <span className="text-[10px] text-slate-500 uppercase">
                        {records.length} inherited
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    {unviewedCount > 0 && (
                        <span
                            className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: `${accentHex}25`, color: accentHex }}
                        >
                            {unviewedCount} unread
                        </span>
                    )}
                    <button
                        onClick={onOpenFull}
                        className="text-[10px] font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-wider hover:underline flex items-center gap-1"
                        title="Open full viewer"
                    >
                        <OpenInFull fontSize="inherit" /> Full view
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-2">
                {records.map((rec) => {
                    const isViewed = rec.hasBeenViewed || localViewed[rec.id];
                    const isExpanded = expandedId === rec.id;
                    const isPending = pendingId === rec.id;
                    return (
                        <div
                            key={rec.id}
                            className={`bg-slate-900/60 border rounded-lg transition-colors ${isExpanded ? 'border-slate-600' : 'border-slate-700 hover:border-slate-600'}`}
                            style={isExpanded ? { borderLeftWidth: 3, borderLeftColor: accentHex } : undefined}
                        >
                            <button
                                onClick={() => handleExpand(rec)}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={`text-sm font-bold truncate ${isExpanded ? 'text-white' : 'text-slate-200'}`}>
                                            {rec.accountName}
                                        </span>
                                        {!isViewed && (
                                            <span
                                                className="size-2 rounded-full shrink-0"
                                                style={{ backgroundColor: accentHex }}
                                                title="Not yet reviewed"
                                            />
                                        )}
                                        {isViewed && (
                                            <CheckCircleOutline style={{ fontSize: 12, color: `${accentHex}99` }} className="shrink-0" />
                                        )}
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                        {ACCOUNT_TYPE_LABEL[rec.accountType] ?? rec.accountType}
                                        {rec.industry && ` · ${rec.industry}`}
                                        {rec.contractValue && ` · ${rec.contractValue}`}
                                    </p>
                                </div>
                                {rec.status && (
                                    <span
                                        className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border shrink-0 ${STATUS_TONE[rec.status] ?? 'text-slate-300 bg-slate-700/40 border-slate-600'}`}
                                    >
                                        {rec.status.replace('-', ' ')}
                                    </span>
                                )}
                                <ChevronRight
                                    fontSize="small"
                                    className="text-slate-500 shrink-0 transition-transform"
                                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}
                                />
                            </button>

                            {isExpanded && (
                                <div className="px-4 pb-4 pt-1 border-t border-slate-700/60 space-y-3">
                                    {isPending && (
                                        <p className="text-[10px] animate-pulse" style={{ color: accentHex }}>Recording review…</p>
                                    )}

                                    {rec.summary && (
                                        <p className="text-xs text-slate-300 leading-relaxed">{rec.summary}</p>
                                    )}

                                    {rec.relationshipOwner && (
                                        <p className="text-[10px] uppercase tracking-wider text-slate-500">
                                            Inherited from <span className="text-slate-300 normal-case font-medium">{rec.relationshipOwner}</span>
                                        </p>
                                    )}

                                    {rec.keyContacts && rec.keyContacts.length > 0 && (
                                        <div>
                                            <h4 className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: `${accentHex}cc` }}>
                                                <PersonOutline fontSize="inherit" /> Key contacts
                                            </h4>
                                            <ul className="space-y-1">
                                                {rec.keyContacts.map((c, i) => (
                                                    <li key={i} className="text-xs text-slate-200">
                                                        <span className="font-bold">{c.name}</span>
                                                        <span className="text-slate-400"> · {c.title}</span>
                                                        {c.notes && <p className="text-[11px] text-slate-400 italic mt-0.5">{c.notes}</p>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {rec.openItems && rec.openItems.length > 0 && (
                                        <div>
                                            <h4 className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: `${accentHex}cc` }}>
                                                <WarningAmber fontSize="inherit" /> Open items
                                            </h4>
                                            <ul className="space-y-1">
                                                {rec.openItems.map((item, i) => (
                                                    <li
                                                        key={i}
                                                        className="text-xs text-slate-200 border-l-2 px-2 py-0.5"
                                                        style={{ borderLeftColor: `${accentHex}99`, backgroundColor: `${accentHex}10` }}
                                                    >
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {rec.recentActivity && rec.recentActivity.length > 0 && (
                                        <div>
                                            <h4 className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: `${accentHex}cc` }}>
                                                <History fontSize="inherit" /> Recent activity
                                            </h4>
                                            <ol className="border-l border-slate-700 pl-3 space-y-1.5">
                                                {rec.recentActivity.slice(0, 4).map((act, i) => (
                                                    <li key={i} className="relative">
                                                        <span
                                                            className="absolute -left-[0.95rem] top-1.5 size-1.5 rounded-full"
                                                            style={{ backgroundColor: `${accentHex}cc` }}
                                                        />
                                                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                                                            {act.date} · {act.type}
                                                        </div>
                                                        <p className="text-xs text-slate-200">{act.summary}</p>
                                                    </li>
                                                ))}
                                            </ol>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default AccountHistoryPanel;
